import { useId, useMemo, useState } from 'react';
import { ConfirmDialog } from '../../job-roles/components/ConfirmDialog';
import { healthcareErrorMessage } from '../healthcareErrors';
import { healthcareRatesGateway } from '../index';
import { HEALTHCARE_TERM_HINTS, industryTagLabel, matchesSuggestion, type Discipline } from '../types';
import { AddDisciplineDialog } from './AddDisciplineDialog';
import '../../job-roles/job-roles.css';
import '../healthcare-rates.css';

export interface WorkerCategoriesSectionProps {
  /** Every worker category including inactive ones; this section filters what it shows. */
  categories: Discipline[];
  tags: string[];
  industryLabel: string | null;
  defaultTags: string[];
  healthcare: boolean;
  canManage: boolean;
  /** Re-reads the catalog after a change. */
  onChanged: () => void;
  /** Lets the page open the add dialog from its action bar: each change of the number opens it once. */
  addRequest?: number | undefined;
}

/**
 * Worker categories (called disciplines in many healthcare organizations): the kind of worker, such as Electrician, Server or Registered Nurse. EPN's
 * suggested starting categories are read-only; the company adds its own, deactivates ones it no longer uses and reactivates them later. A category that an
 * employee or a payroll record uses is deactivated, never deleted, so history keeps reading correctly. Nothing here sets pay, overtime status or a legal
 * classification.
 */
export function WorkerCategoriesSection({ categories, tags, industryLabel, defaultTags, healthcare, canManage, onChanged, addRequest }: WorkerCategoriesSectionProps) {
  const uid = useId();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [mine, setMine] = useState(tags.length > 0);
  const [adding, setAdding] = useState(false);
  const [lastRequest, setLastRequest] = useState(addRequest ?? 0);
  const [confirming, setConfirming] = useState<Discipline | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The page's "Add Worker Category" button asks this section to open its dialog (derived from props during render, not an effect).
  if ((addRequest ?? 0) !== lastRequest) {
    setLastRequest(addRequest ?? 0);
    if (canManage) setAdding(true);
  }

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return categories
      .filter((c) => (showInactive || c.isActive)
        && (!(mine && tags.length > 0) || !c.isSystem || matchesSuggestion(c.industries, tags))
        && (!q || c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)))
      .sort((a, b) => Number(b.isSystem) - Number(a.isSystem) || a.name.localeCompare(b.name));
  }, [categories, search, showInactive, mine, tags]);

  async function reactivate(c: Discipline) {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await healthcareRatesGateway.reactivateDiscipline(c.id, c.concurrencyVersion);
      setStatus(`${c.name} was reactivated.`);
      onChanged();
    } catch (err) {
      setError(healthcareErrorMessage(err, 'Could not reactivate the worker category.'));
    } finally {
      setBusy(false);
    }
  }

  async function deactivate(c: Discipline) {
    setBusy(true);
    setConfirmError(null);
    try {
      await healthcareRatesGateway.deactivateDiscipline(c.id, c.concurrencyVersion);
      setConfirming(null);
      setStatus(`${c.name} was deactivated. Employees, rates and past payroll that use it still show it.`);
      onChanged();
    } catch (err) {
      setConfirmError(healthcareErrorMessage(err, 'Could not deactivate the worker category.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="hdc-section" aria-labelledby={`${uid}-title`} id="worker-categories">
      <div className="section-header">
        <h3 id={`${uid}-title`}>Worker categories</h3>
        {canManage && (
          <button type="button" className="jr-btn jr-btn--primary" onClick={() => { setAdding(true); setStatus(null); }}><span aria-hidden="true">+ </span>Add worker category</button>
        )}
      </div>
      <p className="jr-hint">
        The kind of worker: for example Electrician, Server, Teacher or Registered Nurse. EPN&apos;s defaults are starting points for you to configure; they are not legal job
        classifications and they do not set pay or overtime status. Add your own for anything missing.
        {healthcare ? ` ${HEALTHCARE_TERM_HINTS.workerCategory}` : ''}
      </p>
      {!canManage && <p className="jr-readonly" role="note">You can see worker categories but not change them.</p>}
      {status && <p className="jr-ok" role="status">{status}</p>}
      {error && <p className="jr-error jr-error--block" role="alert">{error}</p>}

      <div className="jr-toolbar">
        <div className="jr-field jr-field--search">
          <label htmlFor={`${uid}-search`}>Search worker categories</label>
          <input id={`${uid}-search`} type="search" placeholder="Name, code or description" value={search} onChange={(e) => setSearch(e.target.value)} autoComplete="off" />
        </div>
        <div className="jr-filters" role="group" aria-label="Worker category filters">
          {tags.length > 0 && industryLabel && (
            <label className="hdc-check">
              <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
              <span>Only defaults suggested for {industryLabel}</span>
            </label>
          )}
          <label className="hdc-check">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            <span>Show inactive</span>
          </label>
        </div>
      </div>

      <div className="jr-table-scroll" role="region" aria-label="Worker categories (scrolls sideways on a narrow screen)" tabIndex={0}>
        <table className="jr-table" aria-label="Worker categories">
          <thead>
            <tr>
              <th scope="col">Name</th><th scope="col">Code</th><th scope="col">Source</th><th scope="col">Suggested for</th><th scope="col">Status</th><th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="muted">No worker categories match.</td></tr>}
            {rows.map((c) => (
              <tr key={c.id}>
                <th scope="row">{c.name}</th>
                <td>{c.code}</td>
                <td>{c.isSystem ? <span className="hdc-badge">EPN default</span> : <span className="hdc-badge hdc-badge--ok">Your company</span>}</td>
                <td>{c.industries.length === 0 ? 'Any' : c.industries.map(industryTagLabel).join(', ')}</td>
                <td><span className={`hdc-badge${c.isActive ? ' hdc-badge--ok' : ''}`}>{c.isActive ? 'Active' : 'Inactive'}</span></td>
                <td>
                  {c.isSystem ? (
                    <span className="muted">Read-only</span>
                  ) : canManage ? (
                    <div className="jr-actions">
                      {c.isActive ? (
                        <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" aria-label={`Deactivate ${c.name}`} disabled={busy} onClick={() => { setConfirming(c); setConfirmError(null); }}>Deactivate</button>
                      ) : (
                        <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" aria-label={`Reactivate ${c.name}`} disabled={busy} onClick={() => void reactivate(c)}>Reactivate</button>
                      )}
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {adding && (
        <AddDisciplineDialog
          defaultTags={defaultTags}
          industryLabel={industryLabel}
          healthcare={healthcare}
          onClose={() => setAdding(false)}
          onAdded={(added) => { setAdding(false); setStatus(`${added.name} was added.`); onChanged(); }}
        />
      )}
      {confirming && (
        <ConfirmDialog
          title="Deactivate worker category"
          confirmLabel="Deactivate"
          busy={busy}
          error={confirmError}
          onCancel={() => setConfirming(null)}
          onConfirm={() => void deactivate(confirming)}
        >
          <p>
            Deactivate <strong>{confirming.name}</strong>? It will no longer be offered when you choose a worker category. Employees, rates and past payroll that use it keep showing it,
            and you can reactivate it later.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}
